import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Loader2, Send, Quote, MessageSquarePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitReview, getReviews } from "@/lib/api";

const StarRating = ({ value, onChange, size = 26, readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange(n)}
            data-testid={readOnly ? undefined : `star-${n}`}
            className={`${readOnly ? "cursor-default" : "cursor-pointer transition-transform hover:scale-110"}`}
            aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
          >
            <Star size={size} className={active ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
          </button>
        );
      })}
    </div>
  );
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const EMPTY = { nom: "", commerce: "", note: 0, commentaire: "" };

export default function AvisPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const data = await getReviews();
      setReviews(data);
    } catch {
      // silencieux
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const average = reviews.length
    ? (reviews.reduce((s, r) => s + r.note, 0) / reviews.length).toFixed(1)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.commentaire.trim()) {
      toast.error("Merci d'indiquer votre nom et un commentaire.");
      return;
    }
    if (!form.note) {
      toast.error("Merci de donner une note.");
      return;
    }
    setSubmitting(true);
    try {
      await submitReview(form);
      toast.success("Merci pour votre avis !");
      setForm(EMPTY);
      await load();
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="avis-page" className="bg-white">
      {/* Header */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-3">Avis clients</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Ce que disent les commerçants
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Découvrez les retours de nos clients et partagez votre propre expérience.
          </p>
          {average && (
            <div className="mt-8 inline-flex items-center gap-3 bg-white rounded-2xl shadow-sm border border-slate-100 px-6 py-4">
              <span className="font-heading text-4xl font-bold text-slate-900">{average}</span>
              <div className="text-left">
                <StarRating value={Math.round(average)} readOnly size={18} />
                <p className="text-sm text-slate-500 mt-1" data-testid="reviews-count">
                  {reviews.length} avis client{reviews.length > 1 ? "s" : ""}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquarePlus className="text-green-600" size={22} />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Laissez votre avis</h2>
              </div>
              <form data-testid="review-form" onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-5">
                <div className="space-y-2">
                  <Label>Votre note</Label>
                  <StarRating value={form.note} onChange={(n) => setForm({ ...form, note: n })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-nom">Votre nom *</Label>
                  <Input id="r-nom" data-testid="review-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Marie D." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-commerce">Votre commerce</Label>
                  <Input id="r-commerce" data-testid="review-commerce" value={form.commerce} onChange={(e) => setForm({ ...form, commerce: e.target.value })} placeholder="Salon de coiffure" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r-comment">Votre commentaire *</Label>
                  <Textarea id="r-comment" data-testid="review-commentaire" rows={4} value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} placeholder="Partagez votre expérience..." />
                </div>
                <button
                  type="submit"
                  data-testid="review-submit-button"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-xl px-6 py-3 font-semibold transition-colors"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                  {submitting ? "Envoi..." : "Publier mon avis"}
                </button>
              </form>
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-slate-400">
                <Loader2 size={28} className="animate-spin" />
              </div>
            ) : reviews.length === 0 ? (
              <div data-testid="reviews-empty" className="text-center py-16 bg-slate-50 rounded-2xl">
                <Quote size={32} className="text-green-600 mx-auto mb-4" />
                <p className="text-slate-600 text-lg font-medium">Soyez le premier à laisser un avis !</p>
                <p className="text-slate-400 mt-1">Votre retour aidera d'autres commerçants.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="reviews-list">
                {reviews.map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
                    data-testid={`review-card-${i}`}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6"
                  >
                    <StarRating value={r.note} readOnly size={18} />
                    <p className="text-slate-700 leading-relaxed my-4">"{r.commentaire}"</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                      <div className="h-10 w-10 rounded-full bg-green-600 text-white flex items-center justify-center font-heading font-bold">
                        {r.nom.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 leading-none">{r.nom}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {r.commerce ? `${r.commerce} · ` : ""}{formatDate(r.created_at)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
